import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-payment.svg';
import { useAppBranding } from '../../config/branding';
import DefaultApiLimit from '../../config/defaultLimit';
import { PaymentsInterFace } from '../../interface/payment-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedDevice } from '../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disablePaymentManagementApplied,
  fetchAllPayments,
  fetchListPaymentMethodFilters,
  updatePaymentManagementFilters,
  setSortData,
} from '../../redux/slices/PaymentManagementSlice';
import { setSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
// import PageTitle from '../../shared-components/page-title/PageTitle';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import EndConsumerSidePanel from '../../shared-components/table-side-panel/EndConsumerSidePanel';
import TransactionSidePanel from '../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { getDaysBetween } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import paymentsApi from '../../utils/api/payments';

/**
 * Payments management page. Displays a paginated, filterable, sortable
 * table of payment transactions, with CSV/PDF export and side panels for
 * device and end-consumer details.
 * @returns {JSX.Element} Payments management page.
 */
function PaymentManagement() {
  const branding = useAppBranding();
  const { t } = useTranslation('transaction');
  const dispatch = useAppDispatch();
  const {
    payments,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.payments);

  const navigate = useNavigate();
  const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Opens the selected device's details, either in the current tab (side
   * panel navigation) or in a new browser tab.
   * @param {any} _ - Unused row data argument.
   * @param {boolean} newTab - Whether to open the device details in a new tab.
   */
  const handleOpenModelView = (_: any, newTab: boolean) => {
    if (deviceDetails) {
      dispatch(
        setSelectedDevice({
          id: deviceDetails?.id,
          name: deviceDetails?.chargingPointName,
          chargingPointSn: deviceDetails?.chargingPointSn,
          modelSeries: deviceDetails?.modelSeries,
        }),
      );

      if (newTab) {
        const url = `/ev-admin/device-management/device/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `../device-management/device/${deviceDetails.id}/device-details`,
          search: `?searchBy=${deviceDetails.chargingPointName}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof PaymentsInterFace,
      sortAllow: true,
      /**
       * Renders the transaction date cell.
       * @param {PaymentsInterFace} rowData - Row data for the current transaction.
       * @returns {JSX.Element} Span containing the formatted date.
       */
      customData: (rowData: PaymentsInterFace) => <span>{rowData?.date}</span>,
    },
    {
      label: t('userName'),
      accessorKey: 'userName' as keyof PaymentsInterFace,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sortAllow: true,
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
      /**
       * Renders the user name cell. Guest users are shown as plain text;
       * registered users are shown as a clickable link that opens the
       * end consumer side panel.
       * @param {PaymentsInterFace} rowData - Row data for the current transaction.
       * @returns {JSX.Element} User name cell content.
       */
      customData: (rowData: PaymentsInterFace) => {
        return rowData.isGuestUser ? (
          <p>{rowData.userName}</p>
        ) : (
          <p
            key={rowData._id}
            role="presentation"
            onClick={() => {
              navigate(`?userCustomId=${rowData.userCustomId}`);
              dispatch(
                setSidePanelData({
                  key: 'Transactions',
                  value: rowData._id,
                }),
              );
            }}
            style={{
              cursor: 'pointer',
              color: 'var(--primary-color)',
              textDecoration: 'underline',
            }}>
            {rowData.userName}
          </p>
        );
      },
    },
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof PaymentsInterFace,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('accountCustomId'),
      accessorKey: 'accountCustomId' as keyof PaymentsInterFace,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber' as keyof PaymentsInterFace,
    },
    {
      label: t('email'),
      accessorKey: 'userEmail' as keyof PaymentsInterFace,
      /**
       * Renders the user email cell, falling back to 'N/A' when absent.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} User email cell content.
       */
      customData: (currentRow: PaymentsInterFace) => (
        <div>{currentRow.userEmail ? currentRow.userEmail : 'N/A'}</div>
      ),
    },
    {
      label: t('totalPowerConsumedKW'),
      accessorKey: 'powerConsumedInKwh' as keyof PaymentsInterFace,
      sortAllow: true,
      /**
       * Renders the total power consumed cell, fixed to 3 decimal places.
       * @param {PaymentsInterFace} row - Row data for the current transaction.
       * @returns {JSX.Element} Power consumed cell content.
       */
      customData: (row: PaymentsInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row.powerConsumedInKwh, 3)}</p>
          <span>kW</span>
        </div>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof PaymentsInterFace,
      sortAllow: true,
      /**
       * Renders the payment amount cell alongside the branding currency label.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} Amount cell content.
       */
      customData: (currentRow: PaymentsInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(currentRow.amount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('transactionId'),
      // accessorKey: 'transactionId' as keyof AvailableTransactions,
      accessorKey: 'chargingTransactionObjectId' as keyof PaymentsInterFace,
      sortAllow: false,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: 'chargingTransactionObjectId',
      sidePanelComponent: <TransactionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('paymentIntentId'),
      accessorKey: 'paymentIntendId' as keyof PaymentsInterFace,
      sortAllow: false,
    },
    {
      label: t('directStartTransaction'),
      accessorKey: 'directStartTransactionEvent' as keyof PaymentsInterFace,
      /**
       * Renders the direct start transaction cell as a localized yes/no.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} Direct start transaction cell content.
       */
      customData: (currentRow: PaymentsInterFace) => (
        <div>{currentRow?.directStartTransactionEvent ? t('yes') : t('no')}</div>
      ),
    },
    {
      label: t('freeTransaction'),
      accessorKey: 'isFreeTransaction' as keyof PaymentsInterFace,
      /**
       * Renders the free transaction cell as a localized yes/no.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} Free transaction cell content.
       */
      customData: (currentRow: PaymentsInterFace) => (
        <div>{currentRow?.isFreeTransaction ? t('yes') : t('no')}</div>
      ),
    },
    {
      label: t('rfidCard'),
      accessorKey: 'rfidNo' as keyof PaymentsInterFace,
      /**
       * Renders the RFID card number cell.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} RFID card cell content.
       */
      customData: (currentRow: PaymentsInterFace) => <div>{currentRow?.rfidNo}</div>,
    },
    {
      label: t('paymentStatus'),
      accessorKey: 'paymentStatus' as keyof PaymentsInterFace,
      /**
       * Renders the payment status cell as a success/failed label.
       * @param {PaymentsInterFace} currentRow - Row data for the current transaction.
       * @returns {JSX.Element} Payment status cell content.
       */
      customData: (currentRow: PaymentsInterFace) =>
        currentRow.paymentStatus === 'processed' || !currentRow.paymentStatus ? (
          <div className="text-success">{t('success')}</div>
        ) : (
          <div className="text-danger">{t('failed')}</div>
        ),
    },
    {
      label: t('platformAmount'),
      accessorKey: 'platformAmount' as keyof PaymentsInterFace,
    },
    {
      label: t('sessionAmount'),
      accessorKey: 'sessionAmount' as keyof PaymentsInterFace,
    },
    {
      label: t('networkRevenue'),
      accessorKey: 'networkRevenue' as keyof PaymentsInterFace,
    },
    {
      label: t('sharableRevenue'),
      accessorKey: 'sharableRevenue' as keyof PaymentsInterFace,
    },
  ];

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Column label/accessorKey pairs used for the visible-columns list and
   * for building export rows.
   * @type {Array<{ label: string; accessorKey: string }>}
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('userName'), accessorKey: 'userName' },
    { label: t('chargingPoint'), accessorKey: 'chargingPointSn' },
    { label: t('accountCustomId'), accessorKey: 'accountCustomId' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('email'), accessorKey: 'userEmail' },
    { label: t('totalPowerConsumedKW'), accessorKey: 'powerConsumedInKwh' },
    { label: t('amount'), accessorKey: 'amount' },
    { label: t('transactionId'), accessorKey: 'chargingTransactionObjectId' },
    { label: t('paymentIntentId'), accessorKey: 'paymentIntendId' },
    { label: t('directStartTransaction'), accessorKey: 'directStartTransactionEvent' },
    { label: t('freeTransaction'), accessorKey: 'isFreeTransaction' },
    { label: t('rfidCard'), accessorKey: 'rfidNo' },
    { label: t('paymentStatus'), accessorKey: 'paymentStatus' },
    { label: t('platformAmount'), accessorKey: 'platformAmount' },
    { label: t('sessionAmount'), accessorKey: 'sessionAmount' },
    { label: t('networkRevenue'), accessorKey: 'networkRevenue' },
    { label: t('sharableRevenue'), accessorKey: 'sharableRevenue' },
  ];

  /**
   * Fetches payment details and dispatches them into the Redux store.
   * Blocks the request and shows an error notification when the selected
   * date range exceeds 30 days.
   * @param {any} data - Filter, sort, and pagination parameters for the request.
   */
  const fetchPayments = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      dispatch(fetchAllPayments(data));
    }
  };

  /**
   * Handles table pagination/limit changes by refetching payments with the
   * current filters and sort applied.
   * @param {any} value - New pagination values (e.g. page, limit).
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Columns to include in export, excluding any columns hidden by the user
   * (the serial number column is always kept).
   * @type {Array<{ label: string; accessorKey: string }>}
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Fetches all payments matching the current filters/sort and exports them
   * to CSV or PDF.
   * @param {string} type - Export format, either 'csv' or 'pdf'.
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    paymentsApi
      .fetchPaymentsExportV2(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: PaymentsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDate(row.createdAt) : t('na');

              case 'userName':
                return row.userName || t('na');

              case 'chargingPointSn':
                return row.chargingPointSn || t('na');

              case 'accountCustomId':
                return row.accountCustomId || t('na');

              case 'phoneNumber':
                return row.phoneNumber || t('na');

              case 'powerConsumedInKwh':
                return row.powerConsumedInKwh || '0';

              case 'amount':
                return `${ToFixedValue(row.amount || '0')} ${branding.text.currency}`;

              case 'chargingTransactionObjectId':
                return row.chargingTransactionObjectId || t('na');
              case 'paymentIntendId':
                return row.paymentIntendId || t('na');
              case 'directStartTransactionEvent':
                return row.directStartTransactionEvent ? t('yes') : t('no');
              case 'isFreeTransaction':
                return row.isFreeTransaction ? t('yes') : t('no');
              case 'rfidNo':
                return row.rfidNo || t('na');
              case 'paymentStatus':
                return row.paymentStatus === 'processed' || !row.paymentStatus
                  ? t('processed')
                  : t('failed');

              case 'platformAmount':
                return ToFixedValue(row.platformAmount || '0');

              case 'sessionAmount':
                return ToFixedValue(row.sessionAmount || '0');

              case 'networkRevenue':
                return ToFixedValue(row.networkRevenue || '0');

              case 'sharableRevenue':
                return ToFixedValue(row.sharableRevenue || '0');

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'payments-list.csv');
          } else {
            exportPDF(headers, rows, 'payments-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handles table sort changes by storing the sort state and refetching
   * payments from the first page with the new sort applied.
   * @param {any} sortData - New sort field and direction (sortField, sortBy).
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPayments({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchPayments({ ...queryParameters, page, limit, ...sortValue });
  };

  useEffect(() => {
    if (isFilterUpdated || payments.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPayments({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disablePaymentManagementApplied());
    }
  }, [filters]);
  useEffect(() => {
    dispatch(
      setPageTitle({ title: t('transactions'), icon: <TransactionIcon />, count: totalResults }),
    );
  }, [totalResults, t]);

  return (
    <div className="payment-management-wrap">
      <div className="table-content">
        <CustomTable
          dataKey="Transactions"
          version="v2"
          header={column}
          height={365}
          data={payments}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          onHiddenColumnsChange={setHiddenColumns}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updatePaymentManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListPaymentMethodFilters}
          hidedColumnNames={[
            'sessionAmount',
            'platformAmount',
            'networkRevenue',
            'sharableRevenue',
          ]}
          refreshRequired
          handleRefresh={handleRefresh}
          ReferenceCurrentPages={handleRefresh}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          hoverLabel={t('refresh')}
        />
      </div>
    </div>
  );
}

export default PaymentManagement;
